import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Select, DatePicker, Tag, Row, Col, theme, Button, Tooltip, Descriptions, Image, EmptyState, Space, Progress, Modal, Form, Input, Drawer, message, type TagTone } from '../../ui';
import { Eye, MapPin, Store, User, ShoppingCart, Crosshair, Plus, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst, formatIstTime, formatIstDate } from '../../lib/time';
import { VisitRouteMap, distanceMeters, fmtDist, LatLng } from '../../components/VisitRouteMap';

const TYPE_COLOR: Record<string, TagTone> = { RETAILER: 'blue', FARMER: 'green', PROSPECT: 'gold' };
const mapsUrl = (lat?: string | null, lng?: string | null) =>
  lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : undefined;
const effColor = (s: number) => (s >= 80 ? '#1F8A4C' : s >= 50 ? '#E7B22C' : '#D9363E');
const effTone = (s: number): TagTone => (s >= 80 ? 'green' : s >= 50 ? 'gold' : 'red');

function Kpi({ title, value, color, icon }: { title: string; value: React.ReactNode; color: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-surface shadow-card" style={{ borderLeft: `3px solid ${color}` }}>
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color, fontSize: 18 }}>{icon}</span>
          <div>
            <div style={{ fontSize: 12, color: '#888' }}>{title}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color }}>{value}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function VisitsPage() {
  const { token } = theme.useToken();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'day'), dayjs()]);
  const [userId, setUserId] = useState<string | undefined>();
  const [visitType, setVisitType] = useState<string | undefined>();
  const [hasOrder, setHasOrder] = useState<string | undefined>();
  const [geofence, setGeofence] = useState<string | undefined>();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const canManage = usePermission('VISIT.MANAGE');

  const params = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    };
    if (userId) p.userId = userId;
    if (visitType) p.visitType = visitType;
    if (hasOrder) p.hasOrder = hasOrder;
    if (geofence) p.geofence = geofence;
    return p;
  }, [range, userId, visitType, hasOrder, geofence]);

  const { data, isLoading } = useQuery({
    queryKey: ['visits', params],
    queryFn: () => request<any[]>({ url: '/visits', params }),
    placeholderData: keepPreviousData,
  });

  const { data: users } = useQuery({
    queryKey: ['sales-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
  });

  const rows = data ?? [];
  const kpis = useMemo(() => {
    const withOrder = rows.filter((r: any) => r.orderPlaced).length;
    const outGeo = rows.filter((r: any) => !r.checkInWithinGeofence).length;
    const scores = rows.map((r: any) => Number(r.effectivenessScore)).filter((n: number) => Number.isFinite(n) && n > 0);
    const avgEff = scores.length ? Math.round(scores.reduce((s: number, n: number) => s + n, 0) / scores.length) : 0;
    return { total: rows.length, withOrder, outGeo, avgEff };
  }, [rows]);

  const columns: DataTableColumn<any>[] = [
    { key: 'visitNo', title: 'Visit No', dataIndex: 'visitNo', sortable: true, alwaysVisible: true, width: 130 },
    {
      key: 'staff', title: 'Staff', sortable: true,
      sorter: (a, b) => String(a.staffName ?? '').localeCompare(String(b.staffName ?? '')),
      render: (_: any, r: any) => r.staffName ?? `#${r.userId}`,
    },
    {
      key: 'party', title: 'Visited', alwaysVisible: true,
      render: (_: any, r: any) => r.party ? (
        <div>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{r.party.name ?? '—'}</span>
            {r.visitType && (
              <Tag tone={TYPE_COLOR[r.visitType] ?? 'neutral'} style={{ marginInlineEnd: 0 }}>
                {r.visitType}
              </Tag>
            )}
          </div>
          {r.party.location && <div style={{ fontSize: 11, color: token.colorTextTertiary }}>{r.party.location}</div>}
        </div>
      ) : <span style={{ color: token.colorTextTertiary }}>—</span>,
    },
    { key: 'visitDate', title: 'Date', dataIndex: 'checkInAt', sortable: true, width: 110, render: (v: string) => formatIstDate(v) },
    { key: 'checkInAt', title: 'Check-in', dataIndex: 'checkInAt', sortable: true, render: (v: string) => formatIstTime(v) },
    { key: 'checkOutAt', title: 'Check-out', dataIndex: 'checkOutAt', sortable: true, render: (v: string) => v ? formatIstTime(v) : <Tag tone="blue">Open</Tag> },
    { key: 'durationMin', title: 'Duration', dataIndex: 'durationMin', sortable: true, align: 'right' as const, render: (v: number) => v != null ? `${v} min` : '—' },
    {
      key: 'effectivenessScore', title: 'Effectiveness', dataIndex: 'effectivenessScore', sortable: true, align: 'right' as const,
      render: (v: any) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? <Tag tone={effTone(n)}>{Math.round(n)}</Tag> : '—';
      },
    },
    {
      key: 'checkInWithinGeofence', title: 'Geofence', dataIndex: 'checkInWithinGeofence', sortable: true,
      render: (v: boolean) => v ? <Tag tone="green">In</Tag> : <Tag tone="red">Out</Tag>,
    },
    {
      key: 'orderPlaced', title: 'Order', dataIndex: 'orderPlaced', sortable: true,
      render: (v: boolean) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      key: 'expenseTotal', title: 'Expense', dataIndex: 'expenseTotal', sortable: true, align: 'right' as const,
      render: (v: any) => (Number(v) > 0 ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'),
    },
    { key: 'photosCount', title: 'Photos', dataIndex: 'photosCount', sortable: true, defaultVisible: false, align: 'right' as const },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 50,
      render: (_: any, r: any) => (
        <Tooltip label="View details">
          <Button variant="text" size="sm" aria-label="View details" icon={<Eye size={16} />} onClick={() => setDetailId(String(r.id))} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Visits</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>Log visit</Button>
          ) : undefined}
        </div>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Field visits to retailers & farmers — geo-verified check-in/out, photos and effectiveness.
      </Typography.Paragraph>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Kpi title="Visits" value={kpis.total} color={token.colorPrimary} icon={<Crosshair size={16} />} /></Col>
        <Col xs={12} md={6}><Kpi title="With order" value={kpis.withOrder} color="#1F8A4C" icon={<ShoppingCart size={16} />} /></Col>
        <Col xs={12} md={6}><Kpi title="Avg effectiveness" value={kpis.avgEff} color={effColor(kpis.avgEff)} icon={<Crosshair size={16} />} /></Col>
        <Col xs={12} md={6}><Kpi title="Outside geofence" value={kpis.outGeo} color="#D9363E" icon={<MapPin size={16} />} /></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="visits"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <>
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => r && setRange(r as [Dayjs, Dayjs])} />
              <Select
                placeholder="All staff" allowClear showSearch optionFilterProp="label"
                className="w-[220px]" value={userId} onChange={setUserId}
                options={(users?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))}
              />
              <Select placeholder="Any type" allowClear className="w-[130px]" value={visitType} onChange={setVisitType}
                options={['RETAILER', 'FARMER', 'PROSPECT'].map((t) => ({ value: t, label: t }))} />
              <Select placeholder="Order?" allowClear className="w-[120px]" value={hasOrder} onChange={setHasOrder}
                options={[{ value: 'true', label: 'With order' }, { value: 'false', label: 'No order' }]} />
              <Select placeholder="Geofence" allowClear className="w-[130px]" value={geofence} onChange={setGeofence}
                options={[{ value: 'in', label: 'Inside' }, { value: 'out', label: 'Outside' }]} />
            </>
          }
        />
      </Card>

      <VisitDetailDrawer id={detailId} onClose={() => setDetailId(null)} canManage={canManage} />
      <LogVisitModal open={createOpen} onClose={() => setCreateOpen(false)} users={users?.data ?? []} />
    </div>
  );
}

function VisitDetailDrawer({ id, onClose, canManage }: { id: string | null; onClose: () => void; canManage: boolean }) {
  const qc = useQueryClient();
  const { data: v, isLoading } = useQuery({
    queryKey: ['visit-detail', id],
    queryFn: () => request<any>({ url: `/visits/${id}` }),
    enabled: !!id,
  });
  const [editNotes, setEditNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const saveNotes = useMutation({
    mutationFn: () => request({ url: `/visits/${id}/notes`, method: 'PATCH', data: { notes: notesDraft } }),
    onSuccess: () => {
      message.success('Notes updated');
      qc.invalidateQueries({ queryKey: ['visit-detail', id] });
      qc.invalidateQueries({ queryKey: ['visits'] });
      setEditNotes(false);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const Geo = ({ lat, lng, label }: { lat?: string | null; lng?: string | null; label: string }) => {
    const url = mapsUrl(lat, lng);
    if (!url) return <span>—</span>;
    return <a href={url} target="_blank" rel="noreferrer"><MapPin size={16} /> {label} ({Number(lat).toFixed(4)}, {Number(lng).toFixed(4)})</a>;
  };

  const score = v ? Math.round(Number(v.effectivenessScore) || 0) : 0;
  // Mirror of the backend heuristic, so the admin can see WHY the score is what it is.
  const breakdown = v ? [
    { label: 'Base', got: 50, on: true },
    { label: 'Duration ≥ 10 min', got: 20, on: (v.durationMin ?? 0) >= 10 },
    { label: 'Photos attached', got: 15, on: (v.photosCount ?? 0) > 0 },
    { label: 'Order placed', got: 20, on: !!v.orderPlaced },
    { label: 'Within geofence', got: 5, on: !!v.checkInWithinGeofence },
  ] : [];

  // Valid Indian coords only — treat empty/0 as "not captured".
  const coord = (la: any, lo: any): LatLng | null => {
    const a = Number(la); const b = Number(lo);
    return Number.isFinite(a) && Number.isFinite(b) && a !== 0 && b !== 0 ? { lat: a, lng: b } : null;
  };
  const startPt = v ? coord(v.checkInLat, v.checkInLong) : null;
  const leavePt = v ? coord(v.checkOutLat, v.checkOutLong) : null;
  const sitePt = v?.party ? coord(v.party.latitude, v.party.longitude) : null;
  const dStartLeave = startPt && leavePt ? distanceMeters(startPt, leavePt) : null;
  const dSiteStart = sitePt && startPt ? distanceMeters(sitePt, startPt) : null;

  return (
    <Drawer open={!!id} onClose={onClose} width={480} title={v ? (v.visitNo ?? 'Visit') : 'Visit'}>
      {isLoading || !v ? null : (
        <>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Staff">{v.staffName ?? `#${v.userId}`}{v.staffRole ? ` · ${v.staffRole}` : ''}</Descriptions.Item>
            <Descriptions.Item label="Type"><Tag tone={TYPE_COLOR[v.visitType] ?? 'neutral'}>{v.visitType}</Tag></Descriptions.Item>
            <Descriptions.Item label="Visited">
              {v.party ? (
                <Space>
                  {v.party.type === 'RETAILER' ? <Store size={16} /> : <User size={16} />}
                  <span>{v.party.name ?? '—'}{v.party.mobile ? ` · ${v.party.mobile}` : ''}</span>
                </Space>
              ) : '—'}
              {v.party?.location && <div style={{ fontSize: 12, color: '#888' }}>{v.party.location}</div>}
            </Descriptions.Item>
            <Descriptions.Item label="Started at">{formatIst(v.checkInAt)}</Descriptions.Item>
            <Descriptions.Item label="Left at">{v.checkOutAt ? formatIst(v.checkOutAt) : 'Still here (open)'}</Descriptions.Item>
            <Descriptions.Item label="Duration">{v.durationMin != null ? `${v.durationMin} min` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Geofence">{v.checkInWithinGeofence ? <Tag tone="green">Inside</Tag> : <Tag tone="red">Outside</Tag>}</Descriptions.Item>
            <Descriptions.Item label="Visit start location"><Geo lat={v.checkInLat} lng={v.checkInLong} label="Map" /></Descriptions.Item>
            <Descriptions.Item label="Visit end location"><Geo lat={v.checkOutLat} lng={v.checkOutLong} label="Map" /></Descriptions.Item>
            <Descriptions.Item label="Order">
              {v.orderPlaced && v.orderId ? <Link to={`/orders/${v.orderId}`}>View order</Link> : v.orderPlaced ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>}
            </Descriptions.Item>
            {v.notes && <Descriptions.Item label="Notes">{v.notes}</Descriptions.Item>}
          </Descriptions>

          {(startPt || leavePt) && (
            <>
              <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>Where this visit happened</Typography.Text>
              <Space wrap style={{ marginBottom: 8 }}>
                {dSiteStart != null && (
                  <Tag tone={v.checkInWithinGeofence ? 'green' : 'red'}>
                    Started {fmtDist(dSiteStart)} from customer ({v.checkInWithinGeofence ? 'inside' : 'outside'} geofence)
                  </Tag>
                )}
                {dStartLeave != null
                  ? <Tag tone="blue">Moved {fmtDist(dStartLeave)} from start to end</Tag>
                  : startPt && !leavePt && <Tag tone="blue">Not left yet</Tag>}
              </Space>
              <VisitRouteMap start={startPt} leave={leavePt} site={sitePt} />
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#888', margin: '6px 2px 0' }}>
                {sitePt && <span>🟢 Customer · 100 m geofence</span>}
                <span>🔵 Visit start</span>
                {leavePt && <span>🟠 Visit end</span>}
              </div>
              {!sitePt && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Customer has no saved location, so the distance from the customer isn’t shown.
                </Typography.Text>
              )}
            </>
          )}

          <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
            Effectiveness <Tag tone={effTone(score)} style={{ marginLeft: 6 }}>{score}/100</Tag>
          </Typography.Text>
          <Progress percent={score} strokeColor={effColor(score)} showInfo={false} />
          <div style={{ marginTop: 8 }}>
            {breakdown.map((b) => (
              <div key={b.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: b.on ? '#1F8A4C' : '#bbb' }}>
                <span>{b.on ? '✓' : '○'} {b.label}</span>
                <span>{b.on ? `+${b.got}` : '—'}</span>
              </div>
            ))}
          </div>

          <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
            Expense to reach
            <Tag tone={Number(v.expenseTotal) > 0 ? 'gold' : 'neutral'} style={{ marginLeft: 6 }}>
              ₹{Number(v.expenseTotal ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </Tag>
          </Typography.Text>
          {v.expenses?.length ? (
            <div style={{ fontSize: 12 }}>
              {v.expenses.map((e: any) => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span><Tag>{e.category}</Tag>{e.receiptUrl ? <a href={e.receiptUrl} target="_blank" rel="noreferrer"> receipt</a> : ''}</span>
                  <span>₹{Number(e.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                </div>
              ))}
            </div>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No expense logged for this visit.</Typography.Text>
          )}

          <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>Photos ({v.photos?.length ?? 0})</Typography.Text>
          {v.photos?.length ? (
            <Image.PreviewGroup>
              <Space wrap>
                {v.photos.map((p: any) => (
                  <Image key={p.id} src={p.url} width={104} height={104} style={{ objectFit: 'cover', borderRadius: 8 }} />
                ))}
              </Space>
            </Image.PreviewGroup>
          ) : (
            <EmptyState title="No photos" />
          )}

          {canManage && (
            <>
              <Typography.Text strong style={{ display: 'block', margin: '18px 0 8px' }}>
                Notes / outcome
                {!editNotes && (
                  <Button variant="text" size="sm" icon={<Pencil size={16} />}
                    onClick={() => { setNotesDraft(v.notes ?? ''); setEditNotes(true); }}>Edit</Button>
                )}
              </Typography.Text>
              {editNotes ? (
                <>
                  <Input.TextArea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} />
                  <Space style={{ marginTop: 8 }}>
                    <Button variant="primary" size="sm" loading={saveNotes.isPending} onClick={() => saveNotes.mutate()}>Save</Button>
                    <Button variant="default" size="sm" onClick={() => setEditNotes(false)}>Cancel</Button>
                  </Space>
                </>
              ) : (
                <Typography.Paragraph type={v.notes ? undefined : 'secondary'} style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {v.notes || 'No notes'}
                </Typography.Paragraph>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
}

function LogVisitModal({ open, onClose, users }: { open: boolean; onClose: () => void; users: any[] }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const visitType = Form.useWatch('visitType', form);

  // Party option lists are loaded lazily once the modal is opened.
  const { data: retailers } = useQuery({
    queryKey: ['retailers-pick'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: open,
  });
  const { data: farmers } = useQuery({
    queryKey: ['farmers-pick'],
    queryFn: () => request<any>({ url: '/farmers', params: { pageSize: 200 } }),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: '/visits/admin', method: 'POST',
      data: {
        userId: vals.userId,
        visitType: vals.visitType,
        retailerId: vals.visitType === 'RETAILER' ? vals.retailerId : undefined,
        farmerId: vals.visitType === 'FARMER' ? vals.farmerId : undefined,
        checkInAt: vals.checkInAt.toISOString(),
        checkOutAt: vals.checkOutAt ? vals.checkOutAt.toISOString() : undefined,
        orderId: vals.orderId || undefined,
        notes: vals.notes,
      },
    }),
    onSuccess: () => {
      message.success('Visit logged');
      qc.invalidateQueries({ queryKey: ['visits'] });
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Log visit (on behalf)"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        initialValues={{ visitType: 'RETAILER', checkInAt: dayjs() }}>
        <Form.Item name="userId" label="Staff" rules={[{ required: true, message: 'Pick a staff member' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select staff"
            options={users.map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
        </Form.Item>
        <Form.Item name="visitType" label="Type" rules={[{ required: true }]}>
          <Select options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
        </Form.Item>
        {visitType === 'RETAILER' ? (
          <Form.Item name="retailerId" label="Retailer" rules={[{ required: true, message: 'Pick a retailer' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Select retailer"
              options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.city ? ` · ${r.city}` : ''}` }))} />
          </Form.Item>
        ) : (
          <Form.Item name="farmerId" label="Farmer">
            <Select showSearch allowClear optionFilterProp="label" placeholder="Select farmer (optional)"
              options={(farmers?.data ?? []).map((f: any) => ({ value: String(f.id), label: `${f.fullName}${f.mobile ? ` · ${f.mobile}` : ''}` }))} />
          </Form.Item>
        )}
        <Row gutter={12}>
          <Col span={12}><Form.Item name="checkInAt" label="Check-in" rules={[{ required: true }]}><DatePicker showTime format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
          <Col span={12}><Form.Item name="checkOutAt" label="Check-out (optional)"><DatePicker showTime format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
        </Row>
        <Form.Item name="orderId" label="Linked order ID (optional)"><Input placeholder="Order id if one was placed" /></Form.Item>
        <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} placeholder="Visit outcome / context…" /></Form.Item>
      </Form>
    </Modal>
  );
}
